import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, TextField, Alert
} from '@mui/material';
import axios from 'axios';
import { C } from '../theme';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://api.binko.me/api';

const REASONS = [
  { value: 'scammer',                  label: 'Scammer / Asked for money' },
  { value: 'unsafe_abusive',           label: 'Made me feel unsafe or was abusive' },
  { value: 'inappropriate_sexual',     label: 'Inappropriate romantic or sexual behavior' },
  { value: 'under_18',                 label: 'Seems under 18' },
  { value: 'didnt_match_profile',      label: "Didn't match their profile" },
  { value: 'not_at_venue',             label: 'Not at venue' },
  { value: 'approached_after_decline', label: 'Approached after I declined' },
  { value: 'other',                    label: 'None of the above' },
];

// ── STEP COMPONENTS ──────────────────────────────────────────

function StepMet({ onAnswer }) {
  return (
    <Box sx={{ textAlign: 'center', py: 1 }}>
      <Typography sx={{ fontSize: 15, fontWeight: 600, color: C.textPrimary, mb: 2 }}>
        Did you two meet in person?
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
        <Button variant="contained" onClick={() => onAnswer(true)}
          sx={{ bgcolor: C.teal, color: '#fff', fontWeight: 700, px: 4, '&:hover': { bgcolor: C.teal } }}>
          Yes
        </Button>
        <Button variant="outlined" onClick={() => onAnswer(false)}
          sx={{ borderColor: C.border, color: C.textMuted, px: 4 }}>
          No
        </Button>
      </Box>
    </Box>
  );
}

function StepRating({ onAnswer }) {
  return (
    <Box sx={{ textAlign: 'center', py: 1 }}>
      <Typography sx={{ fontSize: 15, fontWeight: 600, color: C.textPrimary, mb: 2 }}>
        How was your interaction?
      </Typography>
      <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center' }}>
        <Button onClick={() => onAnswer('positive')}
          sx={{ fontSize: 36, lineHeight: 1, background: 'none', border: 'none',
                '&:hover': { background: 'rgba(255,255,255,0.05)' } }}>
          👍
        </Button>
        <Button onClick={() => onAnswer('negative')}
          sx={{ fontSize: 36, lineHeight: 1, background: 'none', border: 'none',
                '&:hover': { background: 'rgba(255,255,255,0.05)' } }}>
          👎
        </Button>
      </Box>
    </Box>
  );
}

function StepReason({ onSubmit, onBack }) {
  const [selected, setSelected] = useState(null);
  const [details, setDetails]   = useState('');

  return (
    <Box sx={{ py: 1 }}>
      <Alert severity="warning" sx={{ mb: 2, fontSize: 12 }} icon={false}>
        Please report genuine cases only. Your reports help us keep the community safe and clean.
      </Alert>
      <Typography sx={{ fontSize: 14, fontWeight: 600, color: C.textPrimary, mb: 1.5 }}>
        What was the main issue?
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {REASONS.map(r => (
          <Box key={r.value}
            onClick={() => setSelected(r.value)}
            sx={{
              px: 2, py: 1.2, borderRadius: 2, cursor: 'pointer',
              border: selected === r.value
                ? '1.5px solid ' + C.yellow
                : '1px solid ' + C.border,
              bgcolor: selected === r.value ? 'rgba(251,188,21,0.08)' : C.bgElevated,
              transition: 'all 0.15s',
            }}>
            <Typography sx={{ fontSize: 13, color: selected === r.value ? C.yellow : C.textPrimary }}>
              {r.label}
            </Typography>
          </Box>
        ))}
      </Box>
      {selected === 'other' && (
        <TextField
          fullWidth multiline rows={3}
          placeholder="Please describe the issue..."
          value={details}
          onChange={e => setDetails(e.target.value)}
          sx={{ mt: 2,
            '& .MuiOutlinedInput-root': { fontSize: 13, color: C.textPrimary,
              '& fieldset': { borderColor: C.border },
              '&:hover fieldset': { borderColor: C.yellow },
            }
          }}
        />
      )}
      <Box sx={{ display: 'flex', gap: 1.5, mt: 2.5 }}>
        <Button fullWidth variant="outlined" onClick={onBack}
          sx={{ borderColor: C.border, color: C.textMuted }}>
          Back
        </Button>
        <Button fullWidth variant="contained" disabled={!selected}
          onClick={() => onSubmit(selected, details)}
          sx={{ bgcolor: C.yellow, color: C.yellowText, fontWeight: 700,
                '&:hover': { bgcolor: C.yellow }, '&.Mui-disabled': { opacity: 0.4 } }}>
          Submit Report
        </Button>
      </Box>
    </Box>
  );
}

function StepDone({ positive }) {
  return (
    <Box sx={{ textAlign: 'center', py: 2 }}>
      <Typography sx={{ fontSize: 28, mb: 1 }}>{positive ? '🙏' : '✅'}</Typography>
      <Typography sx={{ fontSize: 15, fontWeight: 600, color: C.textPrimary, mb: 1 }}>
        {positive ? 'Thanks for the feedback!' : 'Report submitted'}
      </Typography>
      <Typography sx={{ fontSize: 13, color: C.textMuted }}>
        {positive
          ? 'Glad it went well. Hope to see you again!'
          : 'Thank you for helping keep Binko safe.'}
      </Typography>
    </Box>
  );
}

// ── MAIN MODAL ────────────────────────────────────────────────

export default function FeedbackModal({ open, onClose, matchId, reportedId, triggerSource, skipMet = false, initialMetInPerson = null }) {
  const [step, setStep]               = useState(skipMet ? 'rating' : 'met');   // met | rating | reason | done
  const [metInPerson, setMetInPerson] = useState(skipMet ? initialMetInPerson : null);
  const [rating, setRating]           = useState(null);
  const [submitting, setSubmitting]   = useState(false); // eslint-disable-line no-unused-vars
  const [error, setError]             = useState(null);

  const token = localStorage.getItem('binko_token');

  const reset = () => {
    setStep('met');
    setMetInPerson(null);
    setRating(null);
    setSubmitting(false);
    setError(null);
  };

  const handleClose = async () => {
    // If user closes without completing — dismiss so it doesn't reappear
    if (step !== 'done') {
      try {
        await axios.post(
          API_BASE + '/chat/' + matchId + '/feedback',
          { dismiss: true, triggerSource },
          { headers: { Authorization: 'Bearer ' + token } }
        );
      } catch (_) {}
    }
    reset();
    onClose();
  };

  const handleMet = (ans) => {
    setMetInPerson(ans);
    setStep('rating');
  };

  const handleRating = async (r) => {
    setRating(r);
    if (r === 'positive') {
      await submitFeedback(r, null, null);
    } else {
      setStep('reason');
    }
  };

  const submitFeedback = async (r, reason, details) => {
    setSubmitting(true);
    setError(null);
    try {
      await axios.post(
        API_BASE + '/chat/' + matchId + '/feedback',
        {
          metInPerson,
          interactionRating: r,
          reportReason: reason || null,
          reportDetails: details || null,
          triggerSource,
        },
        { headers: { Authorization: 'Bearer ' + token } }
      );
      setStep('done');
    } catch (e) {
      setError(e?.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReasonSubmit = (reason, details) => {
    submitFeedback(rating, reason, details);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { bgcolor: C.bgCard, border: '1px solid ' + C.border, borderRadius: 3, mx: 2 } }}>
      <DialogTitle sx={{ pb: 0.5, color: C.textPrimary, fontWeight: 700, fontSize: 16 }}>
        {step === 'done' ? '' : '💬 Quick Feedback'}
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        {error && <Alert severity="error" sx={{ mb: 2, fontSize: 12 }}>{error}</Alert>}
        {step === 'met'    && <StepMet onAnswer={handleMet} />}
        {step === 'rating' && <StepRating onAnswer={handleRating} />}
        {step === 'reason' && (
          <StepReason
            onSubmit={handleReasonSubmit}
            onBack={() => setStep('rating')}
          />
        )}
        {step === 'done'   && <StepDone positive={rating === 'positive'} />}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 0 }}>
        {step === 'done'
          ? <Button fullWidth variant="contained" onClick={() => { reset(); onClose(); }}
              sx={{ bgcolor: C.teal, color: '#fff', fontWeight: 700, '&:hover': { bgcolor: C.teal } }}>
              Close
            </Button>
          : step !== 'reason' && (
              <Button fullWidth variant="text" onClick={handleClose}
                sx={{ color: C.textMuted, fontSize: 12 }}>
                Skip
              </Button>
            )
        }
      </DialogActions>
    </Dialog>
  );
}
